import { ChangeEvent, useRef, useState } from 'react';

import {
  StorageReference,
  UploadTask,
  getMetadata,
  ref as storageRef,
  uploadBytesResumable,
} from 'firebase/storage';
import { useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import Image from 'next/image';

import { ICompany, ISanitizedCompany } from 'types/company';

import { gradient } from 'constants/theme';

import { isCompanySlugTaken } from 'services/api/firestore/client';
import { getChangedCompanyFields } from 'services/company';
import { isReservedSlug, isValidSlugFormat, slugify } from 'services/slug';
import { getShortUniqueId } from 'services/uniqueId';

import { storage } from 'clients/firebase';

import { useUpdateCompany } from 'hooks/companies/firebase/update';
import { useNotifications } from 'hooks/notifications';

import { ColorPicker } from 'components/creator-toolkit/card-menu/ColorPicker';
import { useProgressDialog } from 'components/shared/ProgressDialog.hooks';
import { Spinner } from 'components/shared/Spinner';
import { Toggle } from 'components/shared/Toggle';
import { TextField } from 'components/shared/forms/TextField';
import { AttachmentIcon } from 'components/shared/icons/AttachmentIcon';
import { InfoIcon } from 'components/shared/icons/InfoIcon';

interface FormData {
  company: ICompany;
  selectedColor: string;
  uploadedFileLight: File;
  uploadedFileDark: File;
  uploadedFileQR: File;
}

interface IProps {
  company: ISanitizedCompany;
  onClose: () => void;
  onSubmit: (slug: string) => void;
}

export const CompanyDrawer = ({
  company,
  onClose: handleClose,
  onSubmit: handleSubmit,
}: IProps) => {
  const { updateCompany } = useUpdateCompany(company.id);

  const { values, initialValues, setFieldValue } = useFormikContext<FormData>();

  const { show, hide } = useProgressDialog();

  const inputRef = useRef<HTMLInputElement>(null);

  const [showFileUploadError, setShowFileUploadError] =
    useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const addNewColor = () => {
    if (values.selectedColor) {
      setFieldValue('company.theming.colors', [
        ...values.company.theming.colors,
        values.selectedColor,
      ]);
      setFieldValue('selectedColor', null);
    }
  };

  const handleFileChangeLight = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const currentFile = event.target.files?.[0];
    if (currentFile) {
      setFieldValue('uploadedFileLight', currentFile);
    }
  };

  const handleFileChangeDark = async (event: ChangeEvent<HTMLInputElement>) => {
    const currentFile = event.target.files?.[0];
    if (currentFile) {
      setFieldValue('uploadedFileDark', currentFile);
    }
  };

  const handleFileChangeQR = async (event: ChangeEvent<HTMLInputElement>) => {
    const currentFile = event.target.files?.[0];
    if (currentFile) {
      setFieldValue('uploadedFileQR', currentFile);
    }
  };

  const handleUploadFile = async (file: File) => {
    const fileName = `${values.company.name}-${getShortUniqueId()}`;
    let fileStorageLocation: string = `${company.id}/company_logos/${fileName}`;
    const fileRef = storageRef(storage, fileStorageLocation);

    const uploadTask = uploadBytesResumable(fileRef, file);
    return await uploadFile(uploadTask, fileRef);
  };

  const uploadFile = async (
    uploadTask: UploadTask,
    fileRef: StorageReference,
  ) => {
    let progress: number = 0;

    uploadTask.on('state_changed', {
      next: e => {
        progress = Math.round((100 / e.totalBytes) * e.bytesTransferred);

        show({ text: `${progress}%` });
      },
      error: e => {
        console.error(e);
        hide();
      },
    });

    const original = await uploadTask;
    if (original.state === 'success') {
      hide();
      const meta = await getMetadata(fileRef);
      return meta.fullPath;
    }
  };

  const { addNotification } = useNotifications();

  const galleryLinkWithAccessKey = `${window.location.protocol}//${window.location.host}/${values.company.slug}/gallery?accessKey=${values.company.galleryAccessKey}`;

  const copyGalleryLinkWithKey = () => {
    navigator.clipboard.writeText(galleryLinkWithAccessKey).then(() => {
      addNotification({ text: 'Link with access key copied to clipboard' });
    });
  };

  const validateSlugChange = async (desiredSlug: string): Promise<boolean> => {
    if (desiredSlug === company.slug) {
      return true;
    }
    if (!isValidSlugFormat(desiredSlug)) {
      addNotification({
        text: 'Invalid company URL. Use 2–80 lowercase letters, numbers or hyphens.',
      });
      return false;
    }
    if (isReservedSlug(desiredSlug)) {
      addNotification({
        text: `"${desiredSlug}" is reserved and cannot be used as a company URL.`,
      });
      return false;
    }
    if (await isCompanySlugTaken(desiredSlug, company.id)) {
      addNotification({
        text: 'This company URL is already taken. Please choose another.',
      });
      return false;
    }
    return true;
  };

  const saveCompany = async () => {
    setIsSaving(true);

    try {
      const colors = values.selectedColor
        ? [...values.company.theming.colors, values.selectedColor]
        : values.company.theming.colors;

      const desiredSlug = slugify(values.company.slug ?? '');
      if (desiredSlug !== values.company.slug) {
        setFieldValue('company.slug', desiredSlug);
      }
      if (!(await validateSlugChange(desiredSlug))) {
        return;
      }

      // Only what the admin changed, so a stale seed or a concurrent edit
      // (another tab, another admin) is never written back over newer data.
      const { theming: _theming, ...fields } = {
        ...values.company,
        slug: desiredSlug,
      };
      const { theming: initialTheming, ...initialFields } =
        initialValues.company;
      const companyToSave = getChangedCompanyFields(initialFields, fields);
      const colorsToSave = isEqual(colors, initialTheming.colors)
        ? undefined
        : colors;
      const hasUploads =
        values.uploadedFileLight ||
        values.uploadedFileDark ||
        values.uploadedFileQR;

      if (!hasUploads) {
        if (!(await updateCompany(companyToSave, colorsToSave))) {
          throw new Error('Company was not saved: no signed-in user');
        }
        handleSubmit(desiredSlug);
        return;
      }

      const logoPathLight = values.uploadedFileLight
        ? await handleUploadFile(values.uploadedFileLight)
        : undefined;
      const logoPathDark = values.uploadedFileDark
        ? await handleUploadFile(values.uploadedFileDark)
        : undefined;
      const logoPathQR = values.uploadedFileQR
        ? await handleUploadFile(values.uploadedFileQR)
        : undefined;

      if (logoPathLight || logoPathDark || logoPathQR) {
        const saved = await updateCompany(
          companyToSave,
          colorsToSave,
          logoPathLight,
          logoPathDark,
          logoPathQR,
        );
        if (!saved) {
          throw new Error('Company was not saved: no signed-in user');
        }
        handleSubmit(desiredSlug);
      } else {
        setShowFileUploadError(true);
      }
    } catch (error) {
      console.error(error);
      addNotification({
        text: 'Could not save workspace settings. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <header className="mb-7 border-b border-b-[#C4C4C4] p-6 sm:px-12 sm:pb-5 sm:pt-7">
        <div className="flex flex-row items-center justify-between space-x-2 text-xl">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold">My workspace</h1>
          </div>

          <button type="button" className="text-3xl" onClick={handleClose}>
            &times;
          </button>
        </div>
      </header>

      <div className="px-[42px]">
        <div className="pb-6">
          <p className="text-lg font-bold text-darkGrey dark:text-white/80">
            Workspace name
          </p>

          <p className="mb-3 text-xs text-grey dark:text-white/60">
            A workspace is a shared space for your company and team to create
            and share content on acehub.
          </p>

          <TextField
            required
            name="company.name"
            type="text"
            className="w-full placeholder:text-sm"
            placeholder={company.name}
            variant="medium"
          />
        </div>

        <div className="pb-6">
          <p className="text-lg font-bold text-darkGrey dark:text-white/80">
            Company domain
          </p>

          <p className="mb-3 text-xs text-grey dark:text-white/60">
            Your workspace will be available to all colleagues with this email
            domain.
          </p>

          <TextField
            required
            name="company.domain"
            type="text"
            className="w-full placeholder:text-sm"
            placeholder={company.domain}
            variant="medium"
          />
        </div>

        <div className="pb-6">
          <p className="text-lg font-bold text-darkGrey dark:text-white/80">
            Company URL
          </p>

          <p className="mb-3 text-xs text-grey dark:text-white/60">
            Your workspace will be accessible via the following URL.
          </p>

          <div className="flex items-center">
            <p className="mr-2 text-xs text-grey dark:text-white/60">
              {process.env.NEXT_PUBLIC_VERCEL_URL}/
            </p>

            <TextField
              required
              name="company.slug"
              type="text"
              className="w-full placeholder:text-sm"
              placeholder={company.slug}
              variant="medium"
            />
          </div>
        </div>

        <div className="pb-6">
          <div className="flex items-center justify-between">
            <p className="text-lg font-bold text-darkGrey dark:text-white/80">
              Make company public
            </p>

            <Toggle
              isChecked={!values.company.private}
              onChange={() => {
                setFieldValue('company.private', !values.company.private);
              }}
              isDisabled={false}
            />
          </div>

          <p className="mb-3 w-3/4 text-xs text-grey dark:text-white/60">
            Anyone can access your workspace with no need to login for with an
            account.
          </p>
        </div>

        {values.company.private ? (
          <div className="pb-6">
            <div className="flex items-center justify-between">
              <p className="text-lg font-bold text-darkGrey dark:text-white/80">
                Share gallery with an access key
              </p>

              <Toggle
                isChecked={!!values.company.galleryAccessKey}
                onChange={() => {
                  setFieldValue(
                    'company.galleryAccessKey',
                    values.company.galleryAccessKey
                      ? ''
                      : `ak_${getShortUniqueId(32)}`,
                  );
                }}
                isDisabled={false}
              />
            </div>

            <p className="mb-3 w-3/4 text-xs text-grey dark:text-white/60">
              Share your gallery with an access key. Anyone with this link can
              access your gallery.
            </p>

            {values.company.galleryAccessKey ? (
              <div className="mt-4 flex flex-col">
                <div className="flex h-fit items-center rounded-[5px] border-[0.5px] bg-white px-2.5 py-2 dark:border-white/[20%] dark:bg-white/[8%]">
                  <span className="mr-2 overflow-x-hidden text-ellipsis whitespace-nowrap text-[13px] leading-[22px] text-grey dark:text-veryLightGrey">
                    {galleryLinkWithAccessKey}
                  </span>

                  <button
                    type="button"
                    onClick={copyGalleryLinkWithKey}
                    className="h-fit whitespace-nowrap rounded-sm bg-bigStoneBlue px-[7px] py-px text-[10px] font-bold leading-[22px] text-white dark:bg-carnationRed dark:text-black"
                  >
                    Share gallery with access key
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="pb-6">
          <p className="text-lg font-bold text-darkGrey dark:text-white/80">
            Colors
          </p>

          <p className="mb-3 text-xs text-grey dark:text-white/60">
            The colors which are preset for creating content.
          </p>

          <div className="flex items-center gap-4">
            {values.company.theming?.colors?.map(color => (
              <div
                key={color}
                className="relative h-8 w-8 rounded-sm border-2 border-bigStoneBlue dark:border-white"
                style={{ backgroundColor: color }}
              >
                <button
                  type="button"
                  aria-label={`Remove color ${color}`}
                  className="absolute -right-2 -top-2 flex h-4 w-4 cursor-pointer items-center justify-center rounded-full bg-carnationRed text-white"
                  onClick={() =>
                    setFieldValue(
                      'company.theming.colors',
                      [...values.company.theming.colors].filter(
                        col => col !== color,
                      ),
                    )
                  }
                >
                  ×
                </button>
              </div>
            ))}

            <ColorPicker name="selectedColor" />

            <button
              type="button"
              aria-label="Add color"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-bigStoneBlue text-xl text-white dark:bg-carnationRed"
              onClick={() => addNewColor()}
            >
              +
            </button>
          </div>
        </div>

        <div className="pb-6">
          <p className="text-lg font-bold text-darkGrey dark:text-white/80">
            Logo
          </p>

          <p className="mb-3 text-xs text-grey dark:text-white/60">
            A company logo can be displayed in this workspace.
          </p>

          <div className="flex flex-col flex-wrap gap-4 xs:flex-row">
            <div className="flex flex-col">
              <p className="text-center text-xs text-grey dark:text-white/60">
                Light Mode
              </p>

              <div className="relative flex min-h-[100px] w-full flex-col items-center justify-center rounded-[10px] border border-dashed border-carnationRed bg-white dark:bg-eclipse xs:w-[150px]">
                <p className="flex cursor-default flex-col px-2 text-center text-grey dark:text-white/60">
                  <span>Drag a file or</span>

                  <span className="left-0 h-full w-full text-carnationRed">
                    click to upload
                  </span>
                </p>

                {company.logoUrl && !values.uploadedFileLight && (
                  <div className="relative mb-2 mt-1 h-[64px] w-[80%]">
                    <Image
                      src={company.logoUrl}
                      alt="Image"
                      fill
                      sizes="120px"
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                )}

                <input
                  ref={inputRef}
                  className="absolute inset-0 block h-full w-full cursor-pointer appearance-none indent-[-999px] opacity-0"
                  type="file"
                  accept="image"
                  onChange={event => handleFileChangeLight(event)}
                />

                {values.uploadedFileLight && (
                  <div className="absolute bottom-2 flex w-full items-center justify-center px-2">
                    <AttachmentIcon size={16}></AttachmentIcon>

                    <p className="ml-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs">
                      {values.uploadedFileLight.name}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col">
              <p className="text-center text-xs text-grey dark:text-white/60">
                Dark Mode
              </p>

              <div className="relative flex min-h-[100px] w-full flex-col items-center justify-center rounded-[10px] border border-dashed border-carnationRed bg-white dark:bg-eclipse xs:w-[150px]">
                <p className="flex cursor-default flex-col px-2 text-center text-grey dark:text-white/60">
                  <span>Drag a file or</span>

                  <span className="left-0 h-full w-full text-carnationRed">
                    click to upload
                  </span>
                </p>

                {company.logoDarkUrl && !values.uploadedFileDark && (
                  <div className="relative mb-2 mt-1 h-[64px] w-[80%]">
                    <Image
                      src={company.logoDarkUrl}
                      alt="Image"
                      fill
                      sizes="120px"
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                )}

                <input
                  ref={inputRef}
                  className="absolute inset-0 block h-full w-full cursor-pointer appearance-none indent-[-999px] opacity-0"
                  type="file"
                  accept="image"
                  onChange={event => handleFileChangeDark(event)}
                />

                {values.uploadedFileDark && (
                  <div className="absolute bottom-2 flex w-full items-center justify-center px-2">
                    <AttachmentIcon size={16}></AttachmentIcon>

                    <p className="ml-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs">
                      {values.uploadedFileDark.name}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col">
              <p className="text-center text-xs text-grey dark:text-white/60">
                QR
              </p>

              <div className="relative flex min-h-[100px] w-full flex-col items-center justify-center rounded-[10px] border border-dashed border-carnationRed bg-white dark:bg-eclipse xs:w-[150px]">
                <p className="flex cursor-default flex-col px-2 text-center text-grey dark:text-white/60">
                  <span>Drag a file or</span>

                  <span className="left-0 h-full w-full text-carnationRed">
                    click to upload
                  </span>
                </p>

                {company.logoQRUrl && !values.uploadedFileQR && (
                  <div className="relative mb-2 mt-1 h-[64px] w-[80%]">
                    <Image
                      src={company.logoQRUrl}
                      alt="Image"
                      fill
                      sizes="120px"
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                )}

                <input
                  ref={inputRef}
                  className="absolute inset-0 block h-full w-full cursor-pointer appearance-none indent-[-999px] opacity-0"
                  type="file"
                  accept="image"
                  onChange={event => handleFileChangeQR(event)}
                />

                {values.uploadedFileQR && (
                  <div className="absolute bottom-2 flex w-full items-center justify-center px-2">
                    <AttachmentIcon size={16}></AttachmentIcon>

                    <p className="ml-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs">
                      {values.uploadedFileQR.name}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {showFileUploadError && (
            <div className="mt-2 flex items-center gap-x-3.5 rounded-[5px] bg-vividTangerine/25 px-4 py-2.5">
              <div className="shrink-0 text-vividTangerine dark:text-vividTangerine/80">
                <InfoIcon size={20} />
              </div>

              <p className="text-xs text-darkGrey dark:text-white/80">
                We are sorry! There was an error uploading your file.
              </p>
            </div>
          )}
        </div>

        <div className="mb-7 flex justify-end space-x-4">
          <button type="button" onClick={handleClose}>
            Cancel
          </button>

          <button
            type="button"
            className="rounded-lg px-6 py-2 text-white shadow-md dark:text-mineShaftGrey"
            style={{ backgroundImage: gradient }}
            disabled={!values.company.slug || isSaving}
            onClick={saveCompany}
          >
            <span className="flex items-center justify-center">
              <span aria-live="polite">{isSaving ? 'Saving...' : 'Save'}</span>

              {isSaving && (
                <span aria-hidden="true">
                  <Spinner className="ml-2" size={12} />
                </span>
              )}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
